import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import BuilderService from "../../../services/BuilderService";
import { toast } from "react-toastify";
import Loader from "../../../Loader/Loader";
import { useUserDetails } from "../../../../redux/hooks/UserDetails";
import BillingService from "../../../services/BillingService";
import { useTheme } from "../../../../redux/hooks/useTheme";

// Types
interface Feature {
  _id: string;
  name: string;
  heading: string;
  description: string;
  active: boolean;
  limit: number;
}

interface Plan {
  _id: string;
  planName: string;
  display_Plan_name?: string;
  price?: number;
  monthlyPlan: boolean;
  monthlyChatLimit: number;
  totalAppsCount: number;
  totalLeadsCount: number;
  features: Feature[];
  customDomainLimit: boolean;
  inLineEditorLimit: boolean;
  chargeBeePlanId: string;
  uploadImageLimit: number;
  enhanceMagicLimit: number;

  isCustomPlan: boolean;
  showOnPlanPage: boolean;
  userId?: string[];
}
interface PricingCardProps {
  plan: Plan;
  pollForUpgrade: (expectedPlanId: string) => Promise<void>;
  style?: React.CSSProperties;
}
interface DisplayPricingPlanProps {
  onReadyChange?: (ready: boolean) => void;
  className?: string;
  billingPeriod: 'monthly' | 'yearly';
  onBillingPeriodChange: (period: 'monthly' | 'yearly') => void;
}
const builderService = new BuilderService();
// { className = '', style = {}, width = '100%' }
export default function DisplayPricingPlan({ onReadyChange, className = 'w-[100%]', billingPeriod, onBillingPeriodChange }: DisplayPricingPlanProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const { userDetail } = useUserDetails();
  const navigate = useNavigate();
  const [isPolling, setIsPolling] = useState(false);
  const [pollingError, setPollingError] = useState(false);
  const { theme} = useTheme();
  const pollForUpgrade = async (expectedPlanId: string) => {
    setIsPolling(true);
    setPollingError(false);
    let attempts = 0;
    const maxAttempts = 30;
    const billingService = new BillingService();

    const poll = async () => {
      try {
        const res = await billingService.getSubscriptionStatus();
        if (res?.planId === expectedPlanId && res?.status === 'active') {
          setIsPolling(false);
          toast.success('Subscription upgraded successfully!');
          setTimeout(() => window.location.reload(), 1500);
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setIsPolling(false);
          setPollingError(true);
          toast.error('Upgrade is taking longer than expected. Please refresh later.');
        }
      } catch (err) {
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setIsPolling(false);
          setPollingError(true);
          toast.error('Upgrade is taking longer than expected. Please refresh later.');
        }
      }
    };

    poll();
  };
  const handleBillingPeriodChange = (period: 'monthly' | 'yearly') => {
    onBillingPeriodChange(period);
    if (plans.length > 0) {
      const newFilteredPlans = plans.filter((plan) => plan.monthlyPlan === (period === 'monthly'));
      setFilteredPlans(newFilteredPlans);
    }
  };
  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await builderService.getAllPlans();
        if (response.statusCode === 200 && response.data && response.data.length > 0) {
          // Filter plans based on conditions
          const filteredPlans = response.data.filter((plan: Plan) => {
            // If it's a custom plan, check showOnPlanPage and if userId array exists and contains the current user's ID
            if (plan.isCustomPlan) {
              return plan.showOnPlanPage && plan.userId && plan.userId.length > 0 && plan.userId.includes(userDetail?._id);
            }
            // For non-custom plans, check if it's not enterprise when there's a custom plan
            const customPlan = response.data.find((p: Plan) => p.isCustomPlan && p.userId && p.userId.includes(userDetail?._id));
            if (customPlan && (plan.planName === "enterprise_m" || plan.planName === "enterprise_y")) {
              // Show enterprise plan only in the opposite billing period of the custom plan
              return plan.monthlyPlan !== customPlan.monthlyPlan;
            }
            // For other non-custom plans, just check showOnPlanPage
            return plan.showOnPlanPage;
          });
          
          setPlans(filteredPlans);

          // Check if there are any custom plans
          const customPlans = filteredPlans.filter((plan: Plan) => plan.isCustomPlan);
          if (customPlans.length > 0) {
            // Set billing period based on the first custom plan's monthlyPlan value
            onBillingPeriodChange(customPlans[0].monthlyPlan ? 'monthly' : 'yearly');
          }
          
          // Initially filter for monthly plans
          setFilteredPlans(filteredPlans.filter((plan: Plan) => plan.monthlyPlan === true));
          onReadyChange?.(true);
        } else {
          setLoadError(true);
          onReadyChange?.(false);
        }
      } catch (error) {
        setLoadError(true);
        onReadyChange?.(false);
        console.error("Error fetching plans:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, [onReadyChange, userDetail?._id]);

  // Filter plans when toggle changes
  useEffect(() => {
    if (plans.length > 0) {
      setFilteredPlans(plans.filter((plan) => plan.monthlyPlan === (billingPeriod === 'monthly')));
    }
  }, [billingPeriod, plans]);

  // Only set billing period once when plans are first loaded, don't override manual changes
  useEffect(() => {
    if (plans.length > 0 && userDetail?.ogSubscriptionId?.planName) {
      const currentPlan = plans.find(
        plan => plan.planName === userDetail.ogSubscriptionId.planName
      );
      if (currentPlan) {
        const userBillingPeriod = currentPlan.monthlyPlan ? 'monthly' : 'yearly';
        console.log('DisplayPricingPlan: Initial billing period set to:', userBillingPeriod);
        // Only set if it's different from current billing period to avoid overriding manual changes
        if (billingPeriod !== userBillingPeriod) {
          onBillingPeriodChange(userBillingPeriod);
        }
      }
    }
  }, [plans, userDetail?.ogSubscriptionId?.planName]); // Removed onBillingPeriodChange from dependencies

  // Format plan name
  const formatPlanName = (name: string) => {
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Truncate custom plan name
  const truncateCustomPlanName = (name: string | undefined): string => {
    if (!name) return '';
    if (name.length > 12) {
      return name.substring(0, 12) + '...';
    }
    return name;
  };

  // Render feature icon component
  const FeatureIcon = ({ value }: { value: boolean }) => {
    if (value) {
      return (
        <span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M11.6125 1.36104C10.6812 0.574966 9.3188 0.574967 8.38749 1.36104L7.53932 2.07692C7.14349 2.41102 6.65365 2.61394 6.1375 2.65759L5.03155 2.75111C3.81717 2.85381 2.85381 3.81717 2.75111 5.03155L2.65759 6.1375C2.61394 6.65365 2.41102 7.14349 2.07692 7.53934L1.36104 8.38749C0.574966 9.3188 0.574967 10.6812 1.36104 11.6125L2.07692 12.4607C2.41102 12.8565 2.61394 13.3464 2.65759 13.8625L2.75111 14.9685C2.85381 16.1829 3.81717 17.1462 5.03155 17.2489L6.1375 17.3424C6.65365 17.3861 7.14349 17.589 7.53934 17.9231L8.38749 18.639C9.3188 19.425 10.6812 19.425 11.6125 18.639L12.4607 17.9231C12.8565 17.589 13.3464 17.3861 13.8625 17.3424L14.9685 17.2489C16.1829 17.1462 17.1462 16.1829 17.2489 14.9685L17.3424 13.8625C17.3861 13.3464 17.589 12.8565 17.9231 12.4607L18.639 11.6125C19.425 10.6812 19.425 9.3188 18.639 8.38749L17.9231 7.53932C17.589 7.14349 17.3861 6.65365 17.3424 6.1375L17.2489 5.03155C17.1462 3.81717 16.1829 2.85381 14.9685 2.75111L13.8625 2.65759C13.3464 2.61394 12.8565 2.41102 12.4607 2.07692L11.6125 1.36104ZM14.546 8.29555C14.9854 7.85621 14.9854 7.1439 14.546 6.70456C14.1067 6.26521 13.3944 6.26521 12.955 6.70456L8.75054 10.9091L7.04604 9.20456C6.6067 8.76521 5.89439 8.76521 5.45505 9.20456C5.0157 9.6439 5.0157 10.3562 5.45505 10.7956L7.95505 13.2955C8.39439 13.7349 9.1067 13.7349 9.54604 13.2955L14.546 8.29555Z" fill="#01A66F"/>
          </svg>
        </span>
      );
    }
    return <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />;
  };

  // PricingCard Component
  const PricingCard = ({ plan, pollForUpgrade, style = {}  }: PricingCardProps) => {
    const isEnterprise = plan.planName === "enterprise_m" || plan.planName === "enterprise_y";
    const isCustom = plan.isCustomPlan;
    const { userDetail } = useUserDetails();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isChargebeeReady, setIsChargebeeReady] = useState<boolean>(false);
    const [cbInstance, setCbInstance] = useState<any>(null);
    const billingService = new BillingService();
    const isCurrentPlan = userDetail?.ogSubscriptionId?.planName === plan?.planName;
    const location = window.location.pathname;
    const isPlanInfoPage = location.includes('planinfo');
    const isHomePage = !isPlanInfoPage;
    const isProPlan = plan.planName === "pro_m" || plan.planName === "pro_y";

    // Determine which class to apply based on conditions
    const getBackgroundClass = () => {
      if (isPlanInfoPage && isCurrentPlan) {
        return 'current-plan';
      }
      // Only apply pro-active class on home page for pro plans
      if (isHomePage && isProPlan) {
        return 'pro-active';
      }
      return '';
    };

    // Add inline style to remove background for pro plans on planinfo page
    const getInlineStyle = () => {
      if (isPlanInfoPage && isProPlan) {
        return {
          ...style,
          background: 'none',
          backgroundImage: 'none'
        };
      }
      return style;
    };

    useEffect(() => {
      const initChargebee = async () => {
        try {
          if (!window.Chargebee) {
            const script = document.createElement('script');
            script.src = 'https://js.chargebee.com/v2/chargebee.js';
            script.async = true;
            
            await new Promise((resolve, reject) => {
              script.onload = resolve;
              script.onerror = reject;
              document.body.appendChild(script);
            });
          }
    
          window.Chargebee.init({
            site: process.env.REACT_APP_CHARGEBEE_SITE_URL || '',
            publishableKey: process.env.REACT_APP_CHARGEBEE_API_KEY || ''
          });
    
          const instance = window.Chargebee.getInstance();
          setCbInstance(instance);
          setIsChargebeeReady(true);
        } catch (error) {
          console.log('Failed to initialize Chargebee:', error);
        }
      };
    
      initChargebee();
    
      return () => {
        setIsChargebeeReady(false);
        setCbInstance(null);
      };
    }, []);

    const handleButtonClick = async () => {
      if (isEnterprise) {
        navigate("#");
        return;
      }

      if (userDetail?.email && userDetail?.name) {
        try {
          if (userDetail?.ogUser) {
            toast.error(
              <div>
                <p className="font-medium">Organization User</p>
                <p className="text-sm">Please contact your organization administrator to update your plan.</p>
              </div>,
              {
                autoClose: 5000,
                position: "top-center",
              }
            );
            return;
          }

          setIsProcessing(true);
          if (!isChargebeeReady || !cbInstance) {
            console.log('Payment system not ready. Please try again.');
            return;
          }

          const planToChange = plan.chargeBeePlanId;
          const currentPageUrl = window.location.href;
          const currentPlanName = userDetail?.ogSubscriptionId?.planName;
          const subscriptionId = userDetail?.subscriptionId?._id || null;
          const userId = userDetail?._id;
          const response = await billingService.updateSubscription(planToChange, currentPageUrl, currentPlanName, subscriptionId, userId);

          if (response.success && response.hosted_url_data?.hosted_page) {
            const hostedPage = response.hosted_url_data.hosted_page;
            
            cbInstance.openCheckout({
              hostedPage: () => Promise.resolve(hostedPage),
              success: (hostedPageId: string) => {
                // toast.success('Subscription updated successfully!');
                // setTimeout(() => window.location.reload(), 2000);
                // pollForUpgrade(plan.chargeBeePlanId);

              },
              error: (err: any) => {
                console.error('Checkout error:', err);
                // toast.error('Failed to process payment');
                setIsProcessing(false);
              },
              close: () => {
                setIsProcessing(false);
                console.log('Checkout closed');
              },
              step: (step: any) => {
                console.log('Checkout step:', step);
                if (step === 'thankyou-screen') {
                  toast.success("Payment successful! Finalizing your upgrade...");

                   // Close the Chargebee popup
                  if (cbInstance) cbInstance.closeAll();
                  pollForUpgrade(plan.chargeBeePlanId);

                }
              },
              embed: true,
              layout: {
                present: 'popup',
                close_button: true
              }
            });
          } else {
            throw new Error('Invalid hosted page data');
          }
        } catch (error) {
          toast.error('Failed to update subscription');
          console.error('Error:', error);
          setIsProcessing(false);
        }
      } else {
        navigate("/login");
      }
    };

    const handleContactSales = () => {
      // If not logged in or missing user details, open chatbot and send message without user data
      if (!userDetail || !userDetail.email || !userDetail.name) {
        if (window.ChatbotBuilder) {
          if (typeof window.ChatbotBuilder.open === "function") {
            window.ChatbotBuilder.open();
          }
          window.ChatbotBuilder.sendMessage(
            `Hello. I would like to purchase the enterprise plan.`
          );
        }
        return;
      }

      // If user is org user, show error and do not open chatbot
      if (userDetail.ogUser) {
        toast.error(
          <div>
            <p className="font-medium">Organization User</p>
            <p className="text-sm">Please contact your organization administrator to update your plan.</p>
          </div>,
          {
            autoClose: 5000,
            position: "top-center",
          }
        );
        return;
      }

      // If user is logged in and not an org user, open chatbot, set user data, and send message
      if (window.ChatbotBuilder) {
        window.ChatbotBuilder.setUserData({
          name: userDetail.name || '',
          email: userDetail.email || ''
        });
        console.log("User data sent to chatbot:", userDetail.name, userDetail.email);
        if (typeof window.ChatbotBuilder.open === "function") {
          window.ChatbotBuilder.open();
        }
        window.ChatbotBuilder.sendMessage(
          `Hello. I would like to purchase the enterprise plan.`
        );
      }
      
    };

    const checkFeatureValue = (featureKey: string): boolean => {
      switch (featureKey) {
        case "monthlyChatLimit":
          return plan.monthlyChatLimit > 0 || plan.monthlyChatLimit === -1;
        case "totalAppsCount":
          // Special case: -1 means unlimited
          return plan.totalAppsCount > 0 || plan.totalAppsCount === -1;
        case "totalLeadsCount":
          // Special case: -1 means unlimited
          return plan.totalLeadsCount > 0 || plan.totalLeadsCount === -1;
        case "customDomainLimit":
          return plan.customDomainLimit;
        case "inLineEditorLimit":
          return plan.inLineEditorLimit;
        case "enhance_magic":
          return plan.enhanceMagicLimit > 0;
        case "upload_image":
          return plan.uploadImageLimit > 0;
        case "privateProjects":
        case "accountManager":
          return false;
        default:
          return false;
      }
    };

    const getFeatureText = (featureKey: string): React.ReactNode => {
      // For Enterprise plan, show "Custom" for specific features
      if (isEnterprise && (featureKey === "totalAppsCount" || featureKey === "totalLeadsCount" || featureKey === "enhance_magic" || featureKey === "upload_image" || featureKey === "monthlyChatLimit" )) {
        return "Custom";
      }

      // For custom plans, show actual values
      if (isCustom) {
        switch (featureKey) {
          case "monthlyChatLimit":
            return plan.monthlyChatLimit === -1 ? "Unlimited" : (
              <>
                {plan.monthlyChatLimit}
                <span className="hide-on-mobile"> messages</span>
              </>
            );
          case "totalAppsCount":
            return plan.totalAppsCount === -1 ? "Unlimited" : `${plan.totalAppsCount}`;
          case "totalLeadsCount":
            return plan.totalLeadsCount === -1 ? "Unlimited" : `${plan.totalLeadsCount}`;
          case "enhance_magic":
            return plan.enhanceMagicLimit === -1 ? "Unlimited" : `${plan.enhanceMagicLimit}`;
          case "upload_image":
            return plan.uploadImageLimit === -1 ? "Unlimited" : `${plan.uploadImageLimit}`;
          default:
            return "";
        }
      }

      // For regular plans
      switch (featureKey) {
        case "monthlyChatLimit":
          return plan.monthlyChatLimit === -1 ? "Unlimited" : (
            <>
              {plan.monthlyChatLimit}
              <span className="hide-on-mobile"> messages</span>
            </>
          );
        case "totalAppsCount":
          return plan.totalAppsCount === -1 ? "Unlimited" : `${plan.totalAppsCount}`;
        case "totalLeadsCount":
          return plan.totalLeadsCount === -1 ? "Unlimited" : `${plan.totalLeadsCount}`;
        case "enhance_magic":
          return plan.enhanceMagicLimit === -1 ? "Unlimited" : `${plan.enhanceMagicLimit}`;
        case "upload_image":
          return plan.uploadImageLimit === -1 ? "Unlimited" : `${plan.uploadImageLimit}`;
        default:
          return "";
      }
    };

    // if (isPolling) {
    //   return (
    //     <div className="flex flex-col items-center justify-center p-8">
    //       <Loader  text="Processing your upgrade..."  isLoading={true}  imageUrl='https://dlvkyia8i4zmz.cloudfront.net/JllcrYgcTSeRguZ1fEhe_advance_t_b__2___3_.svg'
    //       />
    //       <p className="mt-4 text-gray-600">This may take a few seconds. Please wait...</p>
    //     </div>
    //   );
    // }

    return (
   <div className={`parent-card-plan rounded-[8px] card-blur card-blur-mc ${getBackgroundClass()}`} 
        data-plan={plan.planName} style={getInlineStyle()}>
        <div style={{ width:'100%', ...style }}
      className={`flex flex-col sp-border rounded-lg overflow-hidden plans-parentcard mobile-padding ${
        plan.display_Plan_name?.toLowerCase().includes("pro") ? "border-pink-300" : ""
      }`}>
        <div className="flex flex-col">
         <div className="flex flex-col gap-[5px] h-[247px]">
           <div className="relative">
            <h3 style={{justifyContent:'space-between'}} className="pricing-h p-[28px] flex font-sora pricing-b-bottom mobile-view">
              {isCustom ? truncateCustomPlanName(plan.display_Plan_name) : (plan.display_Plan_name || formatPlanName(plan.planName))}
              <div className=" block md:hidden plan-item-hidden">
                {isEnterprise ? (
                  <span className="pricing-n sm:text-2xl pricing-nres">Custom</span>
                ) : (
                  <>
                    <span className="pricing-n sm:text-2xl pricing-nres">${plan.price} </span>
                    <span className="per-m font-inter">/ {billingPeriod === 'monthly' ? 'Month' : 'Year'}</span>
                  </>
                )}
              </div>
            </h3>
          </div>

          <div className="p-[27px] flex flex-col gap-[28px] mobile-hidden">
            {!isEnterprise ? (
              <>
                <div className="flex items-baseline">
                  <span className="pricing-n sm:text-2xl pricing-nres">${plan.price} </span>
                  <span className="per-m font-inter">/ {billingPeriod === 'monthly' ? 'Month' : 'Year'}</span>
                </div>
                <button 
                onClick={handleButtonClick}
                disabled={isCurrentPlan}
                className={`w-full plan-contact
                   rounded-[12px] h-[44px] py-[12px] px-[24px] text-sm pd-tabrs ${
                  isCurrentPlan ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-pink-50'
                } transition`}
              >
                <span style={{fontWeight: 500}} className={`font-inter text-[14px] ${
                  isCurrentPlan ? 'gradient-text' : 'gradient-text'
                }`}>
                  {isCurrentPlan ? 'Current Plan' : !userDetail ? 'Get Started' : 'Choose Plan'}
                </span>{isCurrentPlan}
              </button>
              </>
            ) : (
              <>
                <span style={{lineHeight:'32px'}} className="pricing-h">Custom</span>
                <button 
                  onClick={handleContactSales}
                  className="w-full text-pink-500 border plan-contact pd-tabrs rounded-[12px] py-[12px] h-[44px] px-[24px] text-sm hover:bg-pink-50 transition"
                >
                  <span style={{fontWeight:500}} className="gradient-text font-inter text-[14px]">Contact Sales</span>
                </button>
              </>
            )}
          </div>
         </div>
        </div>

        <div className="text-center border-gray-200">
          {/* Features with labels and values */}
          {[
            { name: "Messages", description: "/ Month", key: "monthlyChatLimit" },
            { name: "App Count", key: "totalAppsCount" },
            { name: "Lead Count", key: "totalLeadsCount" },
            // { name: "Custom Domain", key: "customDomainLimit" },
            { name: "Inline Editor", key: "inLineEditorLimit", hideOnMobile: false },
            // { name: "Private Projects", key: "privateProjects" },
            // { name: "Account Manager", key: "accountManager" },
            { name: "Enhance Design", description: "/ Tool", key: "enhance_magic", hideOnMobile: false },
            { name: "Upload Image", description: "/ Tool", key: "upload_image", hideOnMobile: false },
          ].map((feature, idx) => (
            <div 
            style={{height:'95px'}}
              key={feature.key} 
              className={`pricing-b h-[95px] flex items-center justify-plans px-[20px] ${
                feature.hideOnMobile ? 'hidden sm:flex' : ''
              }`}
              
            >
              <div className="flex items-center block sm:hidden">
                <span className="pricing-opt font-inter">{feature.name}</span>
                {feature.description && (
                  <span className="ml-1 text-[13px] per-m plans-web-label font-inter">{feature.description}</span>
                )}
              </div>
              <div className="text-xs pricing-txt sm:text-sm mob-res-text font-inter">
                {feature.key === "inLineEditorLimit"
                  ? <FeatureIcon value={checkFeatureValue(feature.key)} />
                  : getFeatureText(feature.key)
                }
              </div>
            </div>
          ))}
          
        </div>
           <div className="pt-0 pr-0 pl-0 pb-[28px] md:p-[28px] flex flex-col gap-[28px] desk-none">
            {!isEnterprise ? (
              <>
                <div className="flex items-baseline mobile-hidden">
                  <span className="pricing-n sm:text-2xl pricing-nres">${plan.price} </span>
                  <span className="per-m font-inter">/ {billingPeriod === 'monthly' ? 'Month' : 'Year'}</span>
                </div>
                <button 
                onClick={handleButtonClick}
                disabled={isCurrentPlan}
                className={`w-full plan-contact
                   rounded-[12px] h-[44px] py-[12px] px-[24px] text-sm pd-tabrs ${
                  isCurrentPlan ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-pink-50'
                } transition`}
              >
                <span style={{fontWeight: 500}} className={`font-inter text-[14px] ${
                  isCurrentPlan ? 'gradient-text' : 'gradient-text'
                }`}>
                  {isCurrentPlan ? 'Current Plan' : !userDetail ? 'Get Started' : 'Choose Plan'}
                </span>{isCurrentPlan}
              </button>
              </>
            ) : (
              <>
                {/* <span style={{lineHeight:'32px'}} className="pricing-h">Custom</span> */}
                <button 
                  onClick={handleContactSales}
                  className="w-full text-pink-500 border plan-contact pd-tabrs rounded-[12px] py-[12px] h-[44px] px-[24px] text-sm hover:bg-pink-50 transition"
                >
                  <span style={{fontWeight:500}} className="gradient-text font-inter text-[14px]">Contact Sales</span>
                </button>
              </>
            )}
          </div>
      </div>  
  </div>
    );
  };

  // CompareCard Component
  const CompareCard = () => {
    const features = [
      { name: "Messages", description: "/ Month", key: "monthlyChatLimit" },
      { name: "Tool Count", key: "totalAppsCount" },
      { name: "Lead Count", key: "totalLeadsCount" },
      // { name: "Custom Domain", key: "customDomainLimit" },
      { name: "Inline Editor", key: "inLineEditorLimit" },
      //   { name: "Private Projects", key: "privateProjects" },
    //   { name: "Account Manager", key: "accountManager" },
      { name: "Enhance Design", description: "/ Tool", key: "enhance_magic" },
      { name: "Upload Image", description: "/ Tool", key: "upload_image" },
    ];

    return (
      <div className="w-full flex flex-col max-w-sm rounded-[12px] plans-parentcard sp-border plans-one card-blur-mc">
        <div className="flex flex-col h-[247px] gap-[50px] py-[27px] px-[19px] mob-hidden-card tab:px-14">
          <div className="flex flex-col gap-[10px]">
            <h3 className="pricing-h pricing-res font-sora hd-hdden">Compare plans</h3>
            <p className="compare-sub font-inter mt-[12px] hd-hdden">
              Explore features across all plans to make the right choice.
            </p>

            <div className="sp-border mt-[26px] p-[2px] rounded-[12px] mob-res-w">
              <div className="flex justify-between plan-p">
                <button
                className="rounded-[12px] px-[12px] py-[10px] text-[12px] font-medium transition-colors"
                style={{
                  background:
                    billingPeriod === "monthly"
                      ? "linear-gradient(107.87deg, #FF3AB6 2.39%, #FB5F65 95.19%)"
                      : "none",
                  color:
                    billingPeriod === "monthly" ? "#ffffff" : theme.monthlyYearly
                }}
                onClick={() => handleBillingPeriodChange("monthly")}
                >
                <span className="font-inter">Monthly</span>
              </button>

              <button
                              className={`rounded-[12px] px-[5px] py-[10px] text-[12px] font-medium transition-colors ${
                  billingPeriod === "yearly" ? "text-white" : ""
                }`}
                style={{
                  width:'125px',
                  color: billingPeriod === "yearly" ? "#ffffff" : theme.monthlyYearly,
                  background: billingPeriod === "yearly"
                    ? "linear-gradient(107.87deg, #FF3AB6 2.39%, #FB5F65 95.19%)"
                    : "none"
                }}
                onClick={() => handleBillingPeriodChange("yearly")}
                >
                <span className="font-inter">Yearly</span>
              </button>

              </div>
              
            </div>
          </div>
        </div>

        <div className="p-0 hidden-label">
          <div>
            {features.map((feature, index) => (
              <div key={index}>
                <div style={{height:'95px'}} className="flex items-center justify-start px-[20px] pricing-b plans-web-label">
                  <div style={{maxHeight:'80px'}} className="flex items-center">
                    <span className="pricing-opt font-inter">{feature.name}</span>
                    {feature.description && (
                      <span className="ml-1 mt-[3px] text-[13px] per-m font-inter">{feature.description}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Loader
        isLoading={isLoading}
        imageUrl="https://dlvkyia8i4zmz.cloudfront.net/JllcrYgcTSeRguZ1fEhe_advance_t_b__2___3_.svg"
        text=""
      />
    );
  }
  if (loadError || filteredPlans.length === 0) {
    return null; // or return <div>None</div>
  }

  return (
    <>
      <style>
        {`
          @media (max-width: 640px) {
            .hide-on-mobile {
              display: none !important;
            }
          }
        `}
      </style>
      <div className="rounded-[20px] plans-parentcard custom-scrollbar mobile-w">
         {isPolling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <Loader isLoading={true} text="Processing your upgrade..." />
        </div>
      )}
        {/* <div className="grid gap-[10px] gap-mc custom-scrollbar device-break display-card pb-[5px] md:gap-[10px] card-gap-mob grid-cols-1 sm:grid-cols-2 lg:grid-cols-[252px_repeat(4,minmax(220px,1fr))] cards-price" style={{ width: 'auto' }} >
          <CompareCard />

          {filteredPlans.map((plan) => (
            <PricingCard key={plan._id} plan={plan} pollForUpgrade={pollForUpgrade} />
          ))}
        </div> */}

        {/* <div
        className="flex gap-[10px] gap-mc custom-scrollbar device-break display-card pb-[5px] md:gap-[10px] card-gap-mob cards-price"
        style={{ width: 'auto' }}
      >
        <div style={{minWidth: '250px'}} className="compare-card w-[250px] sticky top-0 z-10">
          <CompareCard />
        </div>

        <div style={{width:'100%'}}  className="pricing-cards-container flex overflow-x-auto gap-[10px]">
          {filteredPlans.map((plan) => (
            <div  style={{paddingBlock:'2px'}} className="min-w-[220px] w-[100%] planitem" key={plan._id}>
              <PricingCard plan={plan} pollForUpgrade={pollForUpgrade} />
            </div>
          ))}
        </div>
        </div>  */}
        <div
  className="flex flx gap-[8px] custom-scrollbar  display-card pb-[5px] card-gap-mob cards-price"
  style={{ width: '100%' }}
>
  {/* Sticky CompareCard */}
  <div
    className="compare-card  w-[250px] sticky top-0 z-10 bg-white"
    style={{ minWidth: '234px', borderRadius:'14px', height
      :'100%'
     }}
  >
    <CompareCard />
  </div>

  {/* Scrollable Pricing Cards */}
  <div
    // className="pricing-cards-container flex overflow-x-auto gap-[10px] w-full custom-scrollbar overflow-x-auto plan-scr"
    // style={{ paddingBlock: '2px' }}
    className="pricing-cards-container flex gap-[8px] w-full custom-scrollbar plan-scr overflow-x-auto lg:flex-nowrap"
    style={{ paddingBlock: '2px'}}
  >
    {filteredPlans.map((plan) => (
      <div
        key={plan._id}
        className="planitem"
      >
        <PricingCard plan={plan} pollForUpgrade={pollForUpgrade} />
      </div>
    ))}
  </div>
</div>

      </div>
    </>
  );
}